import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { useAuth } from "../../AuthContext";
import { apiFetch } from "../../api";
import './Invites.sass';

const EMPTY_FORM = { firstName: "", lastName: "", hcp: "", email: "" };

export default function Invites() {
    const { leagueId } = useParams();
    const { membership } = useAuth();

    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generatedLinks, setGeneratedLinks] = useState({});
    const [generating, setGenerating] = useState({});
    const [copied, setCopied] = useState({});

    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState(null);

    const isAdmin = membership?.role === "admin";

    useEffect(() => {
        if (!isAdmin) return;
        apiFetch(`/league/${leagueId}/admin/unlinked-players`)
            .then(r => r.json())
            .then(data => setPlayers(Array.isArray(data) ? data : []))
            .catch(err => console.error("Failed to fetch unlinked players:", err))
            .finally(() => setLoading(false));
    }, [leagueId, isAdmin]);

    if (!isAdmin) return <Navigate to={`/league/${leagueId}`} replace />;

    const handleFormChange = (e) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleCreatePlayer = async (e) => {
        e.preventDefault();
        setCreateError(null);
        setCreating(true);
        try {
            const res = await apiFetch(`/league/${leagueId}/admin/players`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            setPlayers(prev => [data, ...prev]);
            setForm(EMPTY_FORM);
            setShowForm(false);
        } catch (err) {
            setCreateError(err.message || "Kunde inte skapa spelare.");
        } finally {
            setCreating(false);
        }
    };

    const generateLink = async (playerId) => {
        setGenerating(prev => ({ ...prev, [playerId]: true }));
        try {
            const res = await apiFetch(`/league/${leagueId}/admin/invites`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ playerId }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            const inviteUrl = `${window.location.origin}/invite/${data.token}`;
            setGeneratedLinks(prev => ({ ...prev, [playerId]: inviteUrl }));
        } catch (err) {
            console.error("Failed to create invite:", err);
        } finally {
            setGenerating(prev => ({ ...prev, [playerId]: false }));
        }
    };

    const copyLink = (playerId) => {
        const link = generatedLinks[playerId];
        if (!link) return;
        navigator.clipboard.writeText(link).then(() => {
            setCopied(prev => ({ ...prev, [playerId]: true }));
            setTimeout(() => setCopied(prev => ({ ...prev, [playerId]: false })), 2000);
        });
    };

    const shareWhatsApp = (playerId, playerName) => {
        const link = generatedLinks[playerId];
        if (!link) return;
        const msg = encodeURIComponent(`Hej ${playerName}! Klicka här för att koppla ditt konto till Svärmorsdrömmar: ${link}`);
        window.open(`https://wa.me/?text=${msg}`, "_blank");
    };

    const shareEmail = (playerId, playerName, emails) => {
        const link = generatedLinks[playerId];
        if (!link) return;
        const to = emails.length > 0 ? emails[0] : "";
        const subject = encodeURIComponent("Inbjudan till Svärmorsdrömmar");
        const body = encodeURIComponent(`Hej ${playerName}!\n\nKlicka på länken nedan för att koppla ditt konto till Svärmorsdrömmar:\n\n${link}\n\nLänken är giltig i 7 dagar.`);
        window.open(`mailto:${to}?subject=${subject}&body=${body}`, "_self");
    };

    return (
        <div className="invites-page">
            <div className="invites-header">
                <div className="invites-header__top">
                    <h2>Spelare &amp; Inbjudningar</h2>
                    <button
                        className="button button--standard"
                        onClick={() => { setShowForm(prev => !prev); setCreateError(null); }}
                    >
                        {showForm ? "Avbryt" : "+ Ny spelare"}
                    </button>
                </div>
                <p className="invites-description">Spelare utan kopplat konto. Generera en länk och skicka till spelaren.</p>
            </div>

            {showForm && (
                <form className="invites-create-form" onSubmit={handleCreatePlayer}>
                    <div className="invites-create-form__grid">
                        <div className="invites-create-form__field">
                            <label htmlFor="firstName">Förnamn *</label>
                            <input
                                id="firstName"
                                name="firstName"
                                type="text"
                                value={form.firstName}
                                onChange={handleFormChange}
                                required
                            />
                        </div>
                        <div className="invites-create-form__field">
                            <label htmlFor="lastName">Efternamn *</label>
                            <input
                                id="lastName"
                                name="lastName"
                                type="text"
                                value={form.lastName}
                                onChange={handleFormChange}
                                required
                            />
                        </div>
                        <div className="invites-create-form__field">
                            <label htmlFor="hcp">HCP</label>
                            <input
                                id="hcp"
                                name="hcp"
                                type="number"
                                step="0.1"
                                value={form.hcp}
                                onChange={handleFormChange}
                            />
                        </div>
                        <div className="invites-create-form__field">
                            <label htmlFor="email">E-post</label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                value={form.email}
                                onChange={handleFormChange}
                            />
                        </div>
                    </div>
                    {createError && <p className="invites-create-form__error">{createError}</p>}
                    <button type="submit" className="button button--standard" disabled={creating}>
                        {creating ? "Skapar..." : "Skapa spelare"}
                    </button>
                </form>
            )}

            {loading ? (
                <p className="invites-loading">Laddar...</p>
            ) : players.length === 0 ? (
                <p className="invites-empty">Alla spelare har kopplat sitt konto.</p>
            ) : (
                <div className="invites-list">
                    {players.map(player => (
                        <div key={player.id} className="invites-item">
                            <div className="invites-item__info">
                                <p className="invites-item__name">{player.firstName} {player.lastName}</p>
                                {player.emails?.length > 0 && (
                                    <p className="invites-item__email">{player.emails.join(", ")}</p>
                                )}
                            </div>

                            <div className="invites-item__actions">
                                {!generatedLinks[player.id] ? (
                                    <button
                                        className="button button--standard"
                                        onClick={() => generateLink(player.id)}
                                        disabled={generating[player.id]}
                                    >
                                        {generating[player.id] ? "Genererar..." : "Generera länk"}
                                    </button>
                                ) : (
                                    <div className="invites-item__link-row">
                                        <input
                                            className="invites-item__link-input"
                                            readOnly
                                            value={generatedLinks[player.id]}
                                            onFocus={e => e.target.select()}
                                        />
                                        <button
                                            className="button button--standard"
                                            onClick={() => copyLink(player.id)}
                                        >
                                            {copied[player.id] ? "Kopierad!" : "Kopiera"}
                                        </button>
                                        <button
                                            className="button button--whatsapp"
                                            onClick={() => shareWhatsApp(player.id, player.firstName)}
                                        >
                                            WhatsApp
                                        </button>
                                        <button
                                            className="button button--email"
                                            onClick={() => shareEmail(player.id, player.firstName, player.emails || [])}
                                        >
                                            E-post
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
